import { appGrep } from './appGrep.js';
import { getOAuth2MachineToMachineFields } from './getOAuth2MachineToMachineFields.js';
import { getRequiredAuthFieldKeys } from './getRequiredAuthFieldKeys.js';
import { getStandardAuthFields } from './getStandardAuthFields.js';

// Field configuration mapping for different auth types
const authFieldMap = {
  apiKey: getStandardAuthFields({ authType: 'apiKey' }),
  basic: getStandardAuthFields({ authType: 'basic' }),
  oAuth1: getStandardAuthFields({ authType: 'oAuth1' }),
  oAuth2: getStandardAuthFields({ authType: 'oAuth2' }),
  custom: getStandardAuthFields({ authType: 'custom' }),
  none: getStandardAuthFields({ authType: 'none' })
};

// Helper function to determine field input type
const getFieldType = (field) => {
  if (field?.isSensitive === true || field.key?.match(/\b(api\s?key|password|secret|token)\b/i)) {
    return 'password';
  }
  return 'text';
};

// Helper function to get auth subtitle/description
const getAuthSubtitle = (type, appName) => {
  const subtitles = {
    oAuth2: `Connect ${appName} using OAuth 2.0. You'll be redirected to sign in securely with your ${appName} account.`,
    oAuth1: `Connect ${appName} using OAuth 1.0. You'll need to authorize this application to access your ${appName} account.`,
    basic: `Connect ${appName} using your username and password. Your credentials will be securely stored and encrypted.`,
    apiKey: `Connect ${appName} using an API key. You can find this in your ${appName} account settings or developer portal.`,
    ftp: `Connect to your ${appName} FTP server. Please provide your server details and credentials below.`,
    custom: `Connect ${appName} using custom authentication. Please fill in the required fields below.`,
    none: `No authentication is required to connect to ${appName}. Click Test Connection to proceed.`
  };

  return subtitles[type] || `Connect to ${appName}. Please fill in the required fields below.`;
};

// Helper function to get auth type from app configuration
const getAppAuthType = (app, credential = null) => {
  if (!app) {
    return null;
  }

  // If we have a credential, get auth type from credential meta/secrets
  if (credential) {
    const appAuth = app.authentication?.find((n) => n?.type === credential?.meta?.type || n?.type === credential?.secrets?.type);
    if (appAuth) {
      return appAuth.type;
    }
  }

  // If app has authentication array, use the first one
  if (app.authentication && app.authentication.length > 0) {
    const validAuthentications = app.authentication.filter((n) => n?.hidden !== true);
    return validAuthentications[0].type;
  }

  // If app has authType property directly
  if (app.authType) {
    return app.authType;
  }

  // If app has authTypes array, use the first one
  if (app.authTypes && app.authTypes.length > 0) {
    return app.authTypes[0];
  }

  console.log('⚠️ No authentication type found for app:', app.name || 'Unknown');
  return null;
};

// Helper function to get auth configuration from app
const getAppAuth = (app, authType) => {
  if (!app || !authType) return null;

  const grep = appGrep(app);
  return grep.authByType({ type: authType });
};

// Helper function to get all fields for an auth type (standard + app-specific)
const getAuthFields = (app, authType) => {
  if (!authType) {
    return [];
  }

  const appAuth = getAppAuth(app, authType);
  const baseStandardFields = getStandardAuthFields({ authType, auth: appAuth });
  const additionalFields = (appAuth?.arguments || []).filter((arg) => arg?.hideForEmbedded !== true);

  // Get standard fields for the auth type
  const standardFields = [
    ...baseStandardFields,
    ...getOAuth2MachineToMachineFields({
      auth: appAuth,
      existingFields: [...baseStandardFields, ...additionalFields]
    })
  ];

  // Combine standard fields with app-specific fields
  const allFields = [
    ...standardFields,
    ...additionalFields.map((arg) => ({
      key: arg.key,
      name: arg.name || arg.placeholder,
      placeholder: arg.placeholder || arg.name,
      dataType: arg.dataType || 'string',
      required: arg.required || false,
      defaultValue: arg.defaultValue,
      list: arg.list
    }))
  ];

  return allFields;
};

// Helper function to get required field keys for validation
const getRequiredAuthFields = (authType, app = null) => {
  if (!authType) {
    return [];
  }

  const appAuth = getAppAuth(app, authType);
  const additionalFields = (appAuth?.arguments || []).filter((arg) => arg?.hideForEmbedded !== true);

  return getRequiredAuthFieldKeys({
    authType,
    auth: appAuth,
    appSpecificFields: additionalFields
  });
};

// Helper function to validate authentication values
const validateAuthValues = (authType, values, app = null) => {
  if (!authType) {
    return false;
  }

  if (authType === 'none') {
    return true;
  }

  if (authType === 'oAuth2') {
    if (!app) {
      return true;
    }
    const requiredFields = getRequiredAuthFields(authType, app);
    if (requiredFields.length === 0) {
      return true;
    }
    return requiredFields.every((fieldKey) => values[fieldKey] && String(values[fieldKey]).trim() !== '');
  }

  const requiredFields = getRequiredAuthFields(authType, app);

  // Check if all required fields are filled
  return requiredFields.every((fieldKey) => values[fieldKey] && String(values[fieldKey]).trim() !== '');
};

// Export single authUtils object following the shared utils pattern
export const authUtils = {
  authFieldMap,
  getFieldType,
  getAuthSubtitle,
  getAppAuthType,
  getAppAuth,
  getAuthFields,
  getRequiredAuthFields,
  validateAuthValues
};
